"use client";
import {NeoButton} from "@/components/forms";
import {NeoBreadcrumb, NeoHeader} from "@/components/shared";
import {NeoSwitchInput} from "@/components/shared/neo-switch/neo-switch-input";
import {useNeoContext} from "@/context/neo/neo.context";
import {convertKMTOM, convertMTOKM} from "@/libs/helper";
import {NeoDashboardService} from "@/service/dashboard.service";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import toast from "react-hot-toast";

export const DeviationSettings: React.FC = () => {
  const breadCrumb = [
    {
      link: "/operations",
      label: "More"
    },
    {
      link: "/operations?tabid=settings",
      label: "Settings"
    },
    {
      link: "",
      label: "Deviation Settings"
    }
  ];
  const [loading, setLoading] = useState(false);
  const {filterContext} = useNeoContext();
  const [speedLimit, setSpeedLimit] = useState({
    min: 0,
    max: 0,
    value: 0
  });
  const [distanceLimit, setDistanceLimit] = useState({
    min: 0,
    max: 0,
    value: 0
  });
  const [speedTooltipPosition, setSpeedTooltipPosition] = useState(0);
  const [distanceTooltipPosition, setDistanceTooltipPosition] = useState(0);
  const speedDivRef = useRef<HTMLInputElement>(null);
  const distanceDivRef = useRef<HTMLInputElement>(null);

  const handleTooltipPosition = (divRef: any) => {
    if (!divRef.current) return 0;
    const slider = divRef.current;
    const sliderWidth = slider.offsetWidth;
    const min = parseInt(slider.min, 10);
    const max = parseInt(slider.max, 10);
    const value = parseInt(slider.value, 10);
    const percentage = ((value - min) / (max - min)) * 100;
    return (percentage / 100) * sliderWidth;
  };

  useEffect(() => {
    const speedTooltipposition = handleTooltipPosition(speedDivRef);
    if (speedTooltipposition) {
      setSpeedTooltipPosition(speedTooltipposition);
    }

    const distanceTooltipPosition = handleTooltipPosition(distanceDivRef);
    if (distanceTooltipPosition) {
      setDistanceTooltipPosition(distanceTooltipPosition);
    }
  }, [speedLimit, distanceLimit]);

  useEffect(() => {
    usersetting();
  }, []);

  const usersetting = async () => {
    await NeoDashboardService.onGetSettingDatas().then((response: any) => {
      if (response.data) {
        var responsedatas = response.data;
        if (responsedatas?.status == "success") {
          if (responsedatas?.data) {
            const responsedata = responsedatas?.data;
            if (responsedata?.speed) {
              const speed = responsedata?.speed;
              var speedmin = speed.min;
              var speedmax = speed.max;
              var speedvalue = speed.value;
              setSpeedLimit((prevData: any) => ({
                ...prevData,
                ["min"]: speedmin,
                ["max"]: speedmax,
                ["value"]: speedvalue
              }));
            }
            if (responsedata?.distance) {
              const distance = responsedata?.distance;
              var distancemin: any = parseFloat(convertMTOKM(distance.min));
              var distancemax: any = parseFloat(convertMTOKM(distance.max));
              var distancevalue: any = parseFloat(convertMTOKM(distance.value));
              setDistanceLimit((prevData: any) => ({
                ...prevData,
                ["min"]: distancemin,
                ["max"]: distancemax,
                ["value"]: distancevalue
              }));
            }
          } else {
            toast.error(response?.message);
          }
        } else {
          toast.error(response?.message);
        }
      }
    });
  };

  const speedlimitchange = async (e: ChangeEvent<any>) => {
    const newValue = e.target.value;
    const updatedSpeedLimit = {...speedLimit, value: newValue};
    setSpeedLimit(updatedSpeedLimit);
    const speedPosition = handleTooltipPosition(speedDivRef);
    setSpeedTooltipPosition(speedPosition);
    //await updateusersetting(updatedSpeedLimit, distanceLimit);
  };

  const distancelimitchange = async (e: ChangeEvent<any>) => {
    const newValue = Number(e.target.value);
    const updatedDistanceLimit = {...distanceLimit, value: newValue};
    setDistanceLimit(updatedDistanceLimit);
    const distancePosition = handleTooltipPosition(distanceDivRef);
    setDistanceTooltipPosition(distancePosition);
    //await updateusersetting(speedLimit, updatedDistanceLimit);
  };

  /*const onSubmit = async (
    updatedSpeed = speedLimit,
    updatedDistance = distanceLimit
  ) => {*/
  const onSubmit = async () => {
    const requestdatas = {
      settings: [
        {
          type: 1,
          value: speedLimit.value
        },
        {
          type: 2,
          value: convertKMTOM(distanceLimit.value)
        }
      ]
    };
    try {
      const response =
        await NeoDashboardService.onSaveUserSetting(requestdatas);
      if (response?.data?.status === "success") {
        toast.success(response?.data?.message);
        usersetting();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (err) {
      toast.error("Sorry! We have technical issues, please try again later.");
    }
  };

  const onReset = () => {
    usersetting();
  };
  return (
    <>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
        <div className="mb-2 flex-row">
          <NeoHeader message="Deviation Settings"></NeoHeader>
        </div>
        <div className="mb-4 flex-row">
          <NeoBreadcrumb menus={breadCrumb}></NeoBreadcrumb>
        </div>
        <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-2">
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <img
                src="/icons/speed.svg"
                alt="Speed Deviation"
                className="h-7 w-7 flex-shrink-0"
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Speed Deviation
                </h3>
                <p className="text-sm text-gray-500">
                  Monitor vehicle speed violations
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-1 items-center justify-center">
              <div className="relative w-full">
                <div
                  className="absolute -top-6 ml-3 w-12 rounded-md bg-black text-center text-white shadow-md"
                  style={{
                    left: `${speedTooltipPosition}px`,
                    transform: "translateX(-50%)"
                  }}
                >
                  {speedLimit.value}
                </div>
                <input
                  ref={speedDivRef}
                  type="range"
                  min={speedLimit.min}
                  max={speedLimit.max}
                  step="5"
                  value={speedLimit.value}
                  onChange={speedlimitchange}
                  className="h-4 w-full cursor-pointer rounded-full accent-black"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{speedLimit.min} km/h</span>
                  <span>{speedLimit.max} km/h</span>
                </div>
              </div>
            </div>
            <div className="-ml-3 mt-auto flex hidden items-center gap-1">
              <NeoSwitchInput
                label=""
                activeLabel=""
                inActiveLabel=""
                //onValueChange={value => handleSwitchChange("speednotification", value)}
              />
              <h1 className="mt-2 text-base font-medium text-gray-900">
                Enable Notifications
              </h1>
            </div>
          </div>
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <img
                src="/icons/distance.svg"
                alt="Distance Deviation"
                className="h-7 w-7 flex-shrink-0"
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Distance Deviation
                </h3>
                <p className="text-sm text-gray-500">
                  Monitor vehicle distance violations
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-1 items-center justify-center">
              <div className="relative w-full">
                <div
                  className="absolute -top-6 ml-3 w-12 rounded-md bg-black text-center text-white shadow-md"
                  style={{
                    left: `${distanceTooltipPosition}px`,
                    transform: "translateX(-50%)"
                  }}
                >
                  {distanceLimit.value}
                </div>
                <input
                  ref={distanceDivRef}
                  type="range"
                  min={distanceLimit.min}
                  max={distanceLimit.max}
                  step="5"
                  value={distanceLimit.value}
                  onChange={distancelimitchange}
                  className="h-4 w-full cursor-pointer rounded-full accent-black"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{distanceLimit.min} kms</span>
                  <span>{distanceLimit.max} kms</span>
                </div>
              </div>
            </div>
            <div className="-ml-3 mt-auto flex hidden items-center gap-1">
              <NeoSwitchInput
                label=""
                activeLabel=""
                inActiveLabel=""
                //onValueChange={value => handleSwitchChange("distancenotification", value)}
              />
              <h1 className="mt-2 text-base font-medium text-gray-900">
                Enable Notifications
              </h1>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-5">
          <NeoButton type="button" color="white" onClick={onReset}>
            Cancel
          </NeoButton>
          <NeoButton className="" type="submit" onClick={onSubmit}>
            Save
          </NeoButton>
        </div>
      </div>
    </>
  );
};
